import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import OverviewRegisteredOrders from './components/biReports/OverviewRegisteredOrders';
import OverviewRegisteredSales from './components/biReports/OverviewRegisteredSales';
import OverviewRegisteredOrderShare from './components/biReports/OverviewRegisteredOrderShare';
import OverviewRegisteredSalesShare from './components/biReports/OverviewRegisteredSalesShare';

import OverviewRegisteredAOV from './components/biReports/OverviewRegisteredAOV';
import OverviewAnonymousAOV from './components/biReports/OverviewAnonymousAOV';
import OverviewAnonymousOrders from './components/biReports/OverviewAnonymousOrders';
import OverviewAnonymousSales from './components/biReports/OverviewAnonymousSales';

import ClientsActivityEvolution from './components/biReports/ClientsActivityEvolution';
import ClientsActivityComparison from './components/biReports/ClientsActivityComparison';
import ClientsActivityByLocation from './components/biReports/ClientsActivityByLocation';
import ClientsActivityDetailedExport from './components/biReports/ClientsActivityDetailedExport';

import Client from './components/Client';
import BackofficeReportsApi from './components/biReports/backofficeReportsApi';
import CustomPeriodPopover, { businessTodayIso, validateCustomPeriod } from './components/biReports/CustomPeriodPopover';

function formatPeriodRange(period) {
  if (!period?.dateFrom || !period?.dateTo) return '';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const from = new Date(`${period.dateFrom}T12:00:00`).toLocaleDateString('ro-RO', options);
  const to = new Date(`${period.dateTo}T12:00:00`).toLocaleDateString('ro-RO', options);
  return `${from} – ${to}`;
}

function formatUpdateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest',
  });
}

class RapClientsActivity extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      edit: false,
      periodMode: "last_30d",
      dateFrom: '',
      dateTo: '',
      customPeriodOpen: false,
      draftDateFrom: '',
      draftDateTo: '',
      customPeriodError: '',
      location: "0",
      locations: [{ iref: "0", name: "All locations" }],
      locationsLoading: false,
      locationsError: false,
      reportPeriod: null,
      reportUpdatedAt: null,
    };
    this.isMountedFlag = false;
    this.locationsRequest = null;
    this.customPeriodButton = React.createRef();
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadLocations();

    if (this.props.onReady) {
      this.props.onReady();
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    if (this.locationsRequest && typeof this.locationsRequest.abort === 'function') {
      this.locationsRequest.abort();
    }
    this.locationsRequest = null;
  }

  loadLocations = () => {
    const request = BackofficeReportsApi.getLocationsAsync(this.state.client);
    this.locationsRequest = request;
    this.setState({ locationsLoading: true, locationsError: false });

    const handleSuccess = (data) => {
      if (!this.isMountedFlag || this.locationsRequest !== request) return;

      const source = Array.isArray(data?.locations)
        ? data.locations
        : Object.values(data?.locations || {});
      const seen = new Set();
      const locations = source
        .filter((location) => location && location.iref && location.name)
        .map((location) => ({
          iref: String(location.iref),
          name: String(location.name),
        }))
        .filter((location) => {
          if (location.iref === "0" || seen.has(location.iref)) return false;
          seen.add(location.iref);
          return true;
        })
        .sort((left, right) => left.name.localeCompare(right.name));

      this.locationsRequest = null;
      this.setState({
        locations: [{ iref: "0", name: "All locations" }, ...locations],
        locationsLoading: false,
        locationsError: false,
      });
    };

    const handleFailure = (xhr, textStatus) => {
      if (textStatus === 'abort') return;
      if (!this.isMountedFlag || this.locationsRequest !== request) return;
      this.locationsRequest = null;
      this.setState({ locationsLoading: false, locationsError: true });
    };

    if (request && typeof request.done === 'function') {
      request.done(handleSuccess);
      request.fail(handleFailure);
    } else {
      Promise.resolve(request).then(handleSuccess).catch(handleFailure);
    }
  };

  handlePeriodChange = (event, newPeriodMode) => {
    if (newPeriodMode === 'custom') {
      this.setState({
        customPeriodOpen: true,
        draftDateFrom: this.state.dateFrom || this.state.reportPeriod?.dateFrom || businessTodayIso(),
        draftDateTo: this.state.dateTo || this.state.reportPeriod?.dateTo || businessTodayIso(),
        customPeriodError: '',
      });
      return;
    }
    if (!newPeriodMode || newPeriodMode === this.state.periodMode) return;

    this.setState({
      periodMode: newPeriodMode,
      reportPeriod: null,
      reportUpdatedAt: null,
    });
  };

  handleCustomPeriodApply = () => {
    const { draftDateFrom, draftDateTo } = this.state;
    const customPeriodError = validateCustomPeriod(draftDateFrom, draftDateTo);
    if (customPeriodError) {
      this.setState({ customPeriodError });
      return;
    }
    if (this.state.periodMode === 'custom' && this.state.dateFrom === draftDateFrom && this.state.dateTo === draftDateTo) {
      this.setState({ customPeriodOpen: false });
      return;
    }
    this.setState({
      periodMode: 'custom',
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      customPeriodOpen: false,
      reportPeriod: null,
      reportUpdatedAt: null,
    });
  };

  handleLocationChange = (event, option) => {
    const location = String(option?.iref || "0");
    if (location === this.state.location) return;
    this.setState({ location });
  };

  handleReportMeta = (period, updatedAt) => {
    if (!period?.dateFrom || !period?.dateTo) return;
    this.setState({
      reportPeriod: period,
      reportUpdatedAt: updatedAt || new Date().toISOString(),
    });
  };

  render() {
    const {
      client,
      periodMode,
      location,
      locations,
      locationsLoading,
      locationsError,
      reportPeriod,
      reportUpdatedAt,
    } = this.state;
    const periodProps = {
      periodMode,
      ...(periodMode === 'custom' ? { dateFrom: this.state.dateFrom, dateTo: this.state.dateTo } : {}),
    };
    const selectedLocation = locations.find(
      (option) => String(option.iref) === String(location),
    ) || locations[0];
    const rangeLabel = formatPeriodRange(reportPeriod);

    const fullWidthChildSx = {
      width: '100%',
      minWidth: 0,
      '& > *': {
        width: '100% !important',
        maxWidth: '100% !important',
        mx: '0 !important',
        boxSizing: 'border-box',
      },
    };

    const reportBoxSx = {
      width: '100%',
      minWidth: 0,
      height: {
        xs: 380,
        sm: 420,
        md: 460,
        lg: 500,
      },
      minHeight: 0,
      overflow: 'hidden',
      borderRadius: {
        xs: '14px',
        sm: '16px',
      },
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
      border: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      boxSizing: 'border-box',

      '& > *': {
        width: '100% !important',
        maxWidth: '100% !important',
        height: '100% !important',
        maxHeight: '100% !important',
        minHeight: '0 !important',
        mx: '0 !important',
        boxSizing: 'border-box',
      },

      '& .MuiCard-root': {
        width: '100% !important',
        height: '100% !important',
        maxHeight: '100% !important',
        minHeight: '0 !important',
        borderRadius: {
          xs: '14px',
          sm: '16px',
        },
        boxShadow: 'none',
        border: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
      },

      '& .MuiCardContent-root': {
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
      },
    };

    return (
      <Box
        component="main"
        sx={{
          // bgcolor: '#f4f7fb',
          minHeight: '100vh',
          flexGrow: 1,
          width: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          ml: {
            xs: 'calc(50% - 50vw + 4px)',
            sm: 0,
          },
          overflowX: 'hidden',
          pt: { xs: 0.75, sm: 1.5, md: 3 },
          pb: { xs: 1, sm: 2, md: 3 },
          px: { xs: 0.5, sm: 1, md: 2 },
          boxSizing: 'border-box',

          '& .MuiGrid-item': {
            minWidth: 0,
          },
          '& .MuiCard-root': {
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <Grid
          container
          spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            m: 0,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              p: '0 !important',
              mb: { xs: 0.75, md: 1.25 },
            }}
          >
            <Stack
              spacing={{ xs: 1, sm: 1.25 }}
              sx={{
                width: '100%',
                px: { xs: 1.25, sm: 1.75, md: 2 },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                bgcolor: '#ffffff',
                borderRadius: { xs: '14px', sm: '16px' },
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                boxSizing: 'border-box',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' },
                      lineHeight: 1.2,
                      color: '#020617',
                    }}
                  >
                    Clients Activity
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.25,
                      color: '#475569',
                      fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    }}
                  >
                    Registered vs anonymous sales activity
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={periodMode}
                    onChange={this.handlePeriodChange}
                    aria-label="Engagement period"
                    sx={{
                      alignSelf: 'center',
                      flexShrink: 0,
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                      },
                      '& .MuiToggleButton-root': {
                        minWidth: { xs: 34, sm: 42 },
                        px: { xs: 0.75, sm: 1.25 },
                        py: { xs: 0.35, sm: 0.45 },
                        fontSize: { xs: '0.66rem', sm: '0.75rem' },
                        fontWeight: 700,
                        textTransform: 'none',
                        color: '#334155',
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                        '&.Mui-selected': {
                          bgcolor: '#0f172a',
                          color: '#fff',
                          '&:hover': {
                            bgcolor: '#111827',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="today" aria-label="Today" title="Today (in progress)">Today</ToggleButton>
                    <ToggleButton value="yesterday" aria-label="Yesterday" title="Yesterday">YDay</ToggleButton>
                    <ToggleButton value="last_7d">7d</ToggleButton>
                    <ToggleButton value="last_30d">30d</ToggleButton>
                    <ToggleButton value="last_90d">90d</ToggleButton>
                    <ToggleButton
                      ref={this.customPeriodButton}
                      value="custom"
                      aria-haspopup="dialog"
                      aria-expanded={this.state.customPeriodOpen}
                      aria-controls={this.state.customPeriodOpen ? 'activity-custom-period' : undefined}
                      onClick={periodMode === 'custom' ? (event) => this.handlePeriodChange(event, 'custom') : undefined}
                    >
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>

              <CustomPeriodPopover
                id="activity-custom-period"
                open={this.state.customPeriodOpen}
                anchorEl={this.customPeriodButton.current}
                onClose={() => this.setState({ customPeriodOpen: false })}
                dateFrom={this.state.draftDateFrom}
                dateTo={this.state.draftDateTo}
                onDateFromChange={(value) => this.setState({ draftDateFrom: value, customPeriodError: '' })}
                onDateToChange={(value) => this.setState({ draftDateTo: value, customPeriodError: '' })}
                onApply={this.handleCustomPeriodApply}
                error={this.state.customPeriodError}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.65, sm: 1 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box minWidth={0} sx={{ alignSelf: 'flex-start', textAlign: 'left' }}>
                  {rangeLabel && (
                    <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 650 }}>
                      {rangeLabel} · {selectedLocation?.name || 'All locations'}{periodMode === 'today' ? ' · In progress' : ''}
                    </Typography>
                  )}
                  {reportUpdatedAt && (
                    <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.68rem', lineHeight: 1.35 }}>
                      Updated {formatUpdateTime(reportUpdatedAt)}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
                  <ClientsActivityDetailedExport
                    client={client}
                    {...periodProps}
                    location={location}
                    locationName={selectedLocation?.name || 'All locations'}
                  />
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                  width: '100%',
                  p: { xs: 1, sm: 1.15 },
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxSizing: 'border-box',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '10px',
                      bgcolor: 'rgba(26, 35, 126, 0.10)',
                      color: '#1a237e',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocationOnRoundedIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <Box minWidth={0}>
                    <Typography sx={{ color: '#0f172a', fontSize: '0.78rem', fontWeight: 800 }}>
                      Sales location
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.7rem', lineHeight: 1.25 }}>
                      Filters all activity by the restaurant/POS where the sale occurred.
                    </Typography>
                  </Box>
                </Stack>

                <Autocomplete
                  size="small"
                  disableClearable={location === '0'}
                  clearText="Reset to all locations"
                  autoHighlight
                  loading={locationsLoading}
                  options={locations}
                  value={selectedLocation}
                  onChange={this.handleLocationChange}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => String(option.iref) === String(value.iref)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select location"
                      error={locationsError}
                      helperText={locationsError ? 'Locations could not be loaded. All locations remain selected.' : ''}
                      inputProps={{ ...params.inputProps, 'aria-label': 'Sales location' }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnRoundedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    width: { xs: '100%', sm: 260, md: 290 },
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      borderRadius: '10px',
                      fontSize: { xs: '16px', sm: '0.8rem' },
                      fontWeight: 650,
                    },
                    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                      fontSize: { xs: '16px', sm: '0.8rem' },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} sx={{ p: '0 !important' }}>
            <Grid
              container
              spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
              sx={{
                width: '100%',
                m: 0,
              }}
            >
              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewRegisteredOrders
                    {...periodProps}
                    location={location}
                    onReportMeta={this.handleReportMeta}
                  />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewRegisteredSales {...periodProps} location={location} />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewRegisteredOrderShare {...periodProps} location={location} />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewRegisteredSalesShare {...periodProps} location={location} />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ p: '0 !important', mt: { xs: 0.75, md: 1.5 } }}>
            <Grid
              container
              spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
              sx={{
                width: '100%',
                m: 0,
              }}
            >
              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewRegisteredAOV {...periodProps} location={location} />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewAnonymousAOV {...periodProps} location={location} />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewAnonymousOrders {...periodProps} location={location} />
                </Box>
              </Grid>

              <Grid item lg={3} md={6} sm={6} xs={6}>
                <Box sx={fullWidthChildSx}>
                  <OverviewAnonymousSales {...periodProps} location={location} />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={8}
            sx={{
              pl: { xs: '0 !important', md: undefined },
              mt: { xs: 0.75, md: 1.5 },
            }}
          >
            <Box sx={reportBoxSx}>
              <ClientsActivityEvolution
                {...periodProps}
                location={location}
                locationName={selectedLocation?.name || 'All locations'}
              />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={4}
            sx={{
              mt: { xs: 0.75, md: 1.5 },
            }}
          >
            <Box sx={reportBoxSx}>
              <ClientsActivityComparison
                {...periodProps}
                location={location}
                locationName={selectedLocation?.name || 'All locations'}
              />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            sx={{
              pl: { xs: '0 !important', md: undefined },
            }}
          >
            <Box
              sx={{
                ...reportBoxSx,
                height: {
                  xs: 440,
                  sm: 460,
                  md: 500,
                  lg: 540,
                },
              }}
            >
              <ClientsActivityByLocation
                {...periodProps}
                location={location}
                locationName={selectedLocation?.name || 'All locations'}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default RapClientsActivity;
